import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Info, Pencil, Save } from 'lucide-react';
import { PaymentMethodLogo, PAYMENT_METHOD_LABELS, ALL_PAYMENT_METHODS } from '../../../components/ui/PaymentMethodLogo';
import type { Organization } from '../../../types/admin';
import type { OrgSettingsUpdate } from '../../../hooks/useOrgSettings';
import { CalculateTaxesCard } from '../../../components/ui/CalculateTaxesCard';

// ── Types ────────────────────────────────────────────────────────────────

export type TaxMode = 'none' | 'auto' | 'manual';

export interface PaymentsData {
  enablePayments: boolean;
  collectDeposit: boolean;
  depositType: 'fixed' | 'percent';
  depositAmount: number;
  balanceCollection: 'before_move' | 'on_completion' | 'split';
  splitBeforeMovePercent?: number;
  taxMode: TaxMode;
  defaultTaxRate: number;
}

interface PaymentsStepProps {
  org: Organization;
  onChange: (data: PaymentsData) => void;
}

// ── Main component ───────────────────────────────────────────────────────

export function PaymentsStep({ org, onChange }: PaymentsStepProps) {
  const [enablePayments, setEnablePayments] = useState(org.featureCustomerPayments === true);

  // Payment schedule
  const defaults = org.paymentScheduleDefaults;
  const [collectDeposit, setCollectDeposit] = useState(defaults?.collectDeposit ?? false);
  const [depositType, setDepositType] = useState<'fixed' | 'percent'>(defaults?.depositType ?? 'percent');
  const [depositAmount, setDepositAmount] = useState<number | undefined>(defaults?.depositAmount ?? 25);
  const [balanceCollection, setBalanceCollection] = useState<'before_move' | 'on_completion' | 'split'>(
    defaults?.balanceCollection ?? 'on_completion',
  );
  const [splitBeforeMovePercent, setSplitBeforeMovePercent] = useState<number>(
    defaults?.splitBeforeMovePercent ?? 50,
  );

  // Tax — tracked via virtual org for the CalculateTaxesCard
  const [virtualOrg, setVirtualOrg] = useState<Organization>({ ...org });

  // Derive tax mode from virtual org
  const autoEnabled = virtualOrg.calculateTaxes ?? false;
  const savedRate = virtualOrg.defaultTaxRate ?? 0;
  const taxMode: TaxMode = autoEnabled ? 'auto' : (savedRate > 0 ? 'manual' : 'none');

  const isFullDeposit = collectDeposit && depositType === 'percent' && (depositAmount ?? 0) === 100;

  useEffect(() => {
    onChange({
      enablePayments,
      collectDeposit,
      depositType,
      depositAmount: depositAmount ?? 0,
      balanceCollection,
      splitBeforeMovePercent,
      taxMode,
      defaultTaxRate: savedRate,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePayments, collectDeposit, depositType, depositAmount, balanceCollection, splitBeforeMovePercent, taxMode, savedRate]);

  // Handle save from CalculateTaxesCard — update virtual org
  async function handleTaxSave(data: OrgSettingsUpdate) {
    setVirtualOrg(prev => ({ ...prev, ...data } as Organization));
  }

  return (
    <div className="space-y-4">
      {/* ── Enable Payments Toggle ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Customer Payments</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Accept payments from customers through IOMI Pro
          </p>
        </div>
        <div className="px-4 py-4">
          {/* Payment method grid */}
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-3 mb-4">
            {ALL_PAYMENT_METHODS.map((key) => (
              <div key={key} className="flex flex-col items-center gap-1.5 py-2">
                <div className="h-10 flex items-center justify-center">
                  <PaymentMethodLogo method={key} size={32} />
                </div>
                <span className="text-[10px] text-slate text-center leading-tight">
                  {PAYMENT_METHOD_LABELS[key]}
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setEnablePayments(true)}
              className={`py-3 text-sm font-semibold rounded-xl border-2 transition-all ${
                enablePayments
                  ? 'border-plum bg-plum text-white shadow-brand-sm'
                  : 'border-stone bg-white text-midnight hover:border-plum/40'
              }`}
            >
              Yes, set up payments
            </button>
            <button
              type="button"
              onClick={() => setEnablePayments(false)}
              className={`py-3 text-sm font-semibold rounded-xl border-2 transition-all ${
                !enablePayments
                  ? 'border-stone bg-stone/30 text-midnight'
                  : 'border-stone bg-white text-slate hover:border-stone'
              }`}
            >
              Not now
            </button>
          </div>
        </div>
      </section>

      {enablePayments && (
        <>
          {/* ── Payment Schedule (starts in edit mode) ── */}
          <PaymentScheduleCard
            collectDeposit={collectDeposit}
            depositType={depositType}
            depositAmount={depositAmount}
            balanceCollection={balanceCollection}
            splitBeforeMovePercent={splitBeforeMovePercent}
            isFullDeposit={isFullDeposit}
            onCollectDepositChange={setCollectDeposit}
            onDepositTypeChange={setDepositType}
            onDepositAmountChange={setDepositAmount}
            onBalanceCollectionChange={setBalanceCollection}
            onSplitPercentChange={setSplitBeforeMovePercent}
          />

          {/* ── Tax Settings (reuses admin component) ── */}
          <CalculateTaxesCard
            org={virtualOrg}
            onSave={handleTaxSave}
          />

          {/* ── Payment Methods (informational) ── */}
          <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone">
              <p className="font-semibold text-small text-midnight">Payment Methods</p>
              <p className="text-[12px] text-slate leading-snug mt-0.5">
                Configure accepted payment methods
              </p>
            </div>
            <div className="px-4 py-4">
              <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-plum/5 border border-plum/20">
                <Info size={16} className="shrink-0 text-plum mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[13px] text-midnight leading-relaxed">
                    Set up Stripe Connect for credit card payments and your offline methods
                    (Venmo, Cash App, PayPal, Zelle, check, cash) on the Payment Methods page.
                    Each method can have its own identifier (handle, email, etc.) shown to customers.
                  </p>
                  <Link
                    to="/admin/payments/payment-methods"
                    className="inline-flex items-center gap-1.5 mt-2 min-h-[44px] text-[13px] font-semibold text-plum hover:text-rose transition-colors"
                  >
                    Open Payment Methods
                    <ExternalLink size={14} />
                  </Link>
                  <p className="text-[11px] text-slate mt-0.5">
                    Your other wizard answers are saved when you press Continue; come back to the setup from the banner at the top.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// ── Payment Schedule Card (matching admin CustomerPaymentsPage pattern) ──

function PaymentScheduleCard({
  collectDeposit,
  depositType,
  depositAmount,
  balanceCollection,
  splitBeforeMovePercent,
  isFullDeposit,
  onCollectDepositChange,
  onDepositTypeChange,
  onDepositAmountChange,
  onBalanceCollectionChange,
  onSplitPercentChange,
}: {
  collectDeposit: boolean;
  depositType: 'fixed' | 'percent';
  depositAmount: number | undefined;
  balanceCollection: 'before_move' | 'on_completion' | 'split';
  splitBeforeMovePercent: number;
  isFullDeposit: boolean;
  onCollectDepositChange: (v: boolean) => void;
  onDepositTypeChange: (v: 'fixed' | 'percent') => void;
  onDepositAmountChange: (v: number | undefined) => void;
  onBalanceCollectionChange: (v: 'before_move' | 'on_completion' | 'split') => void;
  onSplitPercentChange: (v: number) => void;
}) {
  // Wizard always starts in edit mode
  const [editing, setEditing] = useState(true);

  function getDepositLabel(): string {
    if (!collectDeposit) return 'No';
    if (isFullDeposit) return 'Yes — 100% upfront';
    if (depositType === 'fixed') return `Yes — $${depositAmount ?? 0} fixed`;
    return `Yes — ${depositAmount ?? 0}% of quote`;
  }

  function getBalanceLabel(): string {
    if (!collectDeposit) return 'Full amount due at completion';
    if (isFullDeposit) return 'N/A — full deposit collected at booking';
    switch (balanceCollection) {
      case 'before_move': return 'One payment before the move';
      case 'on_completion': return 'One payment after the move';
      case 'split': {
        const before = splitBeforeMovePercent ?? 50;
        return `Split — ${before}% before move, ${100 - before}% after`;
      }
      default: return 'Not configured';
    }
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Payment Schedule</p>
          <p className="text-[12px] text-slate mt-0.5 leading-snug">
            Default payment schedule for booked jobs.
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-plum border border-plum rounded-lg hover:bg-plum/5 transition-colors"
          >
            <Pencil size={12} />
            Edit
          </button>
        )}
        {editing && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-plum text-white rounded-lg hover:bg-plum/90 transition-colors"
          >
            <Save size={12} />
            Done
          </button>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* — View mode — */}
        {!editing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Collect Deposit
              </p>
              <p className="text-[14px] font-semibold text-midnight">{getDepositLabel()}</p>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Balance Collection
              </p>
              <p className="text-[14px] font-semibold text-midnight">{getBalanceLabel()}</p>
            </div>
          </div>
        )}

        {/* — Edit mode (matching admin PaymentScheduleCard) — */}
        {editing && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Collect deposit? */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                  Collect Deposit
                </p>
                <div className="flex gap-2 max-w-[240px]">
                  <button
                    type="button"
                    onClick={() => onCollectDepositChange(true)}
                    className={`flex-1 h-11 rounded-lg text-[14px] font-semibold border transition-colors
                      ${collectDeposit
                        ? 'bg-plum text-white border-plum'
                        : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onCollectDepositChange(false);
                      onDepositAmountChange(undefined);
                    }}
                    className={`flex-1 h-11 rounded-lg text-[14px] font-semibold border transition-colors
                      ${!collectDeposit
                        ? 'bg-plum text-white border-plum'
                        : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                  >
                    No
                  </button>
                </div>
                {!collectDeposit && (
                  <p className="text-[12px] text-slate mt-2 leading-snug">
                    The full quote amount will be due when the job is complete.
                  </p>
                )}
              </div>

              {/* Deposit amount — only when collecting deposit */}
              {collectDeposit && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                    Deposit Amount
                  </p>

                  {/* Fixed / Percent toggle */}
                  <div className="flex rounded-lg border border-stone overflow-hidden mb-3 max-w-[260px]">
                    <button
                      type="button"
                      onClick={() => onDepositTypeChange('fixed')}
                      className={`flex-1 py-2 text-[13px] font-medium transition-colors
                        ${depositType === 'fixed'
                          ? 'bg-plum text-white'
                          : 'bg-white text-slate hover:bg-mist'}`}
                    >
                      Fixed Amount
                    </button>
                    <button
                      type="button"
                      onClick={() => onDepositTypeChange('percent')}
                      className={`flex-1 py-2 text-[13px] font-medium border-l border-stone transition-colors
                        ${depositType === 'percent'
                          ? 'bg-plum text-white'
                          : 'bg-white text-slate hover:bg-mist'}`}
                    >
                      % of Quote
                    </button>
                  </div>

                  {/* Amount input */}
                  <div className="relative max-w-[200px]">
                    {depositType === 'fixed' ? (
                      <>
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-[14px]">$</span>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={depositAmount ?? ''}
                          onChange={e => {
                            const val = e.target.value;
                            onDepositAmountChange(val === '' ? undefined : Number(val));
                          }}
                          className="w-full h-11 pl-7 pr-3 rounded-lg border border-stone bg-white
                                     text-small text-midnight placeholder:text-slate
                                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                     transition-colors duration-150"
                          placeholder="0"
                        />
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={depositAmount ?? ''}
                          onChange={e => {
                            const val = e.target.value;
                            onDepositAmountChange(val === '' ? undefined : Math.min(100, Number(val)));
                          }}
                          className="w-full h-11 pl-3 pr-8 rounded-lg border border-stone bg-white
                                     text-small text-midnight placeholder:text-slate
                                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                     transition-colors duration-150"
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-[14px]">%</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Balance collection (when deposit is < 100%) */}
            {collectDeposit && !isFullDeposit && (
              <div className="pt-2 border-t border-stone">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                  Balance Collection
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {([
                    { value: 'before_move' as const, label: 'Before the move', desc: 'Customer pays the balance before the move starts.' },
                    { value: 'on_completion' as const, label: 'After the move', desc: 'Customer pays the balance when the job is complete.' },
                    { value: 'split' as const, label: 'Split before & after', desc: 'Balance is split into two payments.' },
                  ]).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        onBalanceCollectionChange(opt.value);
                        if (opt.value === 'split' && !splitBeforeMovePercent) {
                          onSplitPercentChange(50);
                        }
                      }}
                      className={`text-left px-4 py-3 rounded-lg border transition-colors
                        ${balanceCollection === opt.value
                          ? 'border-plum bg-plum/5'
                          : 'border-stone bg-white hover:border-plum/40'}`}
                    >
                      <p className={`text-[14px] font-semibold ${balanceCollection === opt.value ? 'text-plum' : 'text-midnight'}`}>
                        {opt.label}
                      </p>
                      <p className="text-[12px] text-slate mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Split percentage */}
                {balanceCollection === 'split' && (
                  <div className="mt-3 p-3 bg-mist rounded-lg max-w-sm">
                    <p className="text-[13px] font-semibold text-midnight mb-2">
                      Split percentage
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                          Before move
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={1}
                            max={99}
                            step={1}
                            value={splitBeforeMovePercent}
                            onChange={e => {
                              const val = Math.min(99, Math.max(1, Number(e.target.value)));
                              onSplitPercentChange(val);
                            }}
                            className="w-full h-11 pl-3 pr-8 rounded-lg border border-stone bg-white
                                       text-small text-midnight
                                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                       transition-colors duration-150"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-[14px]">%</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                          After move
                        </label>
                        <div className="h-11 px-3 rounded-lg border border-stone bg-mist
                                        flex items-center text-small text-slate">
                          {100 - splitBeforeMovePercent}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
